import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useSettings } from "@/context/SettingsContext";
import { useSpin } from "@/lib/spin";
import SpinStage from "@/components/SpinStage";
import FrankensteinSpinner from "@/components/FrankensteinSpinner";
import { levelLabel } from "@/lib/levels";
import ProductGrid from "@/components/ProductGrid";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { House, Camera, CameraOff, Lightbulb, Shuffle, Gamepad2, ArrowRight, ShoppingBag } from "lucide-react";
import { toast } from "sonner";

const TIPS = [
  "Say yes, and — accept your partner's offer, then add to it.",
  "There are no mistakes, only offers. A slip becomes the scene.",
  "Listen with your whole body. The best line is often a reaction.",
  "Play at the top of your intelligence, and support your ensemble first.",
];

function PromptaneerTab() {
  const { sensoryFriendly } = useSettings();
  const { display, spinning, countdown, run } = useSpin(sensoryFriendly);
  const [categories, setCategories] = useState([]);
  const [categoryId, setCategoryId] = useState("");

  useEffect(() => {
    api.get("/categories").then(({ data }) => {
      setCategories(data);
      if (data[0]) setCategoryId(data[0].id);
    }).catch(() => {});
  }, []);

  const draw = async () => {
    if (!categoryId) return;
    try {
      const { data } = await api.post("/generator/draw", { category_id: categoryId, exclude: [] });
      run(data, data.spin_pool);
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  return (
    <div className="bg-white rounded-2xl border border-[#dfd8da] p-6" data-testid="participant-promptaneer">
      <span className="block text-xs uppercase tracking-[0.22em] font-extrabold text-[#920e12] mb-2" data-testid="participant-promptinator-tag">The Promptinator</span>
      <p className="text-[#545454] mb-4">Pick a category and spin your own prompt to practise a scene at home.</p>
      {categories.length === 0 ? (
        <p className="text-[#545454]">No prompt categories are available yet.</p>
      ) : (
        <div className="space-y-4">
          <select value={categoryId} onChange={(e) => setCategoryId(e.target.value)} data-testid="participant-category"
            className="w-full h-11 px-3 rounded-lg border border-[#d6cecf] bg-white">
            {categories.map((c) => <option key={c.id} value={c.id}>{c.name} ({levelLabel(c.level)})</option>)}
          </select>
          <Button onClick={draw} disabled={spinning} className="w-full h-12 bg-[#920e12] hover:bg-[#550001] text-white font-bold" data-testid="participant-spin">
            <Shuffle className={`mr-2 ${spinning ? "animate-spin" : ""}`} size={18} /> {spinning ? "Spinning…" : "Spin For A Prompt"}
          </Button>
          {display && (
            <div className={`bg-[#221517] text-white rounded-2xl p-8 text-center transition-transform duration-200 ${spinning ? "scale-[1.02]" : ""}`} data-testid="participant-result">
              <SpinStage result={display} spinning={spinning} countdown={countdown} compact wheelItems={categories.map((c) => c.name)} reducedMotion={sensoryFriendly} />
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function ParticipantPortal() {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = searchParams.get("tab") || "classes";
  const setTab = (value) => setSearchParams(value === "classes" ? {} : { tab: value });
  const [classes, setClasses] = useState([]);
  const [participants, setParticipants] = useState([]);
  const [homeprov, setHomeprov] = useState([]);

  const loadHomeprov = () => api.get("/me/homeprov").then(({ data }) => setHomeprov(data)).catch(() => {});

  useEffect(() => {
    api.get("/me/classes").then(({ data }) => setClasses(data)).catch(() => {});
    api.get("/me/participants").then(({ data }) => setParticipants(data)).catch(() => {});
    loadHomeprov();
  }, []);

  const markDone = async (h, done) => {
    try {
      await api.post("/me/homeprov/done", null, { params: { class_id: h.class_id, block_order: h.block_order, done } });
      setHomeprov((hp) => hp.map((x) => (x.class_id === h.class_id && x.block_order === h.block_order ? { ...x, done } : x)));
    } catch { toast.error("Could not update"); }
  };

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12" data-testid="participant-portal">
      <span className="text-xs uppercase tracking-[0.22em] font-extrabold text-[#920e12]">Participant Area</span>
      <h1 className="mt-2 font-display text-4xl text-[#550001]">Hi, {user?.name?.split(" ")[0]}</h1>
      <p className="mt-2 text-[#545454]">Book classes, spin prompts, play games, and practise your Home Improv — all in one place.</p>

      <Tabs value={tab} onValueChange={setTab} className="mt-8">
        <TabsList className="bg-white border border-[#dfd8da] flex-wrap h-auto">
          <TabsTrigger value="classes" data-testid="tab-classes">My Classes</TabsTrigger>
          <TabsTrigger value="shop" data-testid="tab-shop">Shop</TabsTrigger>
          <TabsTrigger value="promptaneer" data-testid="tab-promptaneer">Spin For A Prompt</TabsTrigger>
          <TabsTrigger value="frankenstein" data-testid="tab-frankenstein">Dr. Frankenstein</TabsTrigger>
          <TabsTrigger value="games" data-testid="tab-games">Games</TabsTrigger>
          <TabsTrigger value="homeprov" data-testid="tab-homeprov">Home Improv</TabsTrigger>
          <TabsTrigger value="participants" data-testid="tab-participants">Participants</TabsTrigger>
          <TabsTrigger value="tips" data-testid="tab-tips">Tips</TabsTrigger>
        </TabsList>

        <TabsContent value="promptaneer" className="mt-6">
          <PromptaneerTab />
        </TabsContent>

        <TabsContent value="frankenstein" className="mt-6">
          <FrankensteinSpinner compact />
        </TabsContent>

        <TabsContent value="games" className="mt-6">
          <div className="bg-white rounded-2xl border border-[#dfd8da] p-8 flex flex-col items-start gap-3" data-testid="participant-games">
            <div className="w-12 h-12 rounded-2xl bg-[#920e12] text-white flex items-center justify-center"><Gamepad2 size={24} /></div>
            <h3 className="text-xl font-bold text-[#1a1617]">Explore the games library</h3>
            <p className="text-[#545454]">Browse every game with full descriptions, examples, and ways to play without speaking.</p>
            <Button asChild className="bg-[#920e12] hover:bg-[#550001] text-white" data-testid="participant-games-link">
              <Link to="/games">Open games library <ArrowRight size={16} className="ml-1" /></Link>
            </Button>
          </div>
        </TabsContent>

        <TabsContent value="homeprov" className="mt-6">
          <div className="space-y-3">
            {homeprov.map((h) => (
              <label key={`${h.class_id}-${h.block_order}`} className="flex items-start gap-3 bg-white rounded-2xl border border-[#dfd8da] p-5 cursor-pointer" data-testid={`homeprov-${h.class_id}-${h.block_order}`}>
                <Checkbox checked={h.done} onCheckedChange={(v) => markDone(h, !!v)} data-testid={`homeprov-done-${h.class_id}-${h.block_order}`} className="mt-1 w-6 h-6" />
                <div>
                  <div className="flex items-center gap-2">
                    <House size={16} className="text-[#920e12]" />
                    <span className={`font-bold text-[#1a1617] ${h.done ? "line-through opacity-60" : ""}`}>{h.title}</span>
                  </div>
                  <p className="mt-1 text-sm text-[#545454]">{h.content}</p>
                  <p className="mt-1 text-xs text-[#bf6163]">from {h.class_title}</p>
                </div>
              </label>
            ))}
            {homeprov.length === 0 && <p className="text-[#545454]">No Home Improv assignments yet.</p>}
          </div>
        </TabsContent>

        <TabsContent value="classes" className="mt-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {classes.map((c) => (
              <div key={c.enrollment_id} className="bg-white rounded-2xl border border-[#dfd8da] p-6" data-testid={`my-class-${c.class_id}`}>
                <div className="text-[10px] uppercase tracking-widest font-extrabold text-[#bf6163]">{levelLabel(c.level)} · Session {c.session_no} · Class {c.class_no}</div>
                <h3 className="mt-1 text-xl font-bold text-[#1a1617]">{c.title}</h3>
                <span className="mt-3 inline-block text-sm px-3 py-1 rounded-full bg-[#dcfce7] text-[#15803d] font-semibold capitalize">{c.status}</span>
              </div>
            ))}
            {classes.length === 0 && <p className="text-[#545454]">No classes yet — visit the <strong>Shop</strong> tab to enroll in one.</p>}
          </div>
        </TabsContent>

        <TabsContent value="shop" className="mt-6">
          <div className="mb-4 flex items-center gap-2 text-[#545454]" data-testid="participant-shop">
            <ShoppingBag size={18} className="text-[#920e12]" />
            <p>Browse and enroll in classes. Consent and participant setup happen at secure checkout.</p>
          </div>
          <ProductGrid compact />
        </TabsContent>

        <TabsContent value="participants" className="mt-6">
          <div className="space-y-3">
            {participants.map((p) => {
              const photo = p.consents.find((c) => c.type === "photo_video_release");
              return (
                <div key={p.id} className="bg-white rounded-2xl border border-[#dfd8da] p-5 flex items-center justify-between" data-testid={`participant-${p.id}`}>
                  <div>
                    <div className="text-lg font-bold text-[#1a1617]">{p.name}</div>
                    <div className="text-sm text-[#545454]">DOB {p.date_of_birth}</div>
                  </div>
                  {photo?.granted ? (
                    <span className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-bold bg-[#dcfce7] text-[#15803d]"><Camera size={15} /> Photo OK</span>
                  ) : (
                    <span className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-bold bg-[#fee2e2] text-[#991b1b]"><CameraOff size={15} /> No photo</span>
                  )}
                </div>
              );
            })}
            {participants.length === 0 && <p className="text-[#545454]">No participant profiles yet.</p>}
          </div>
        </TabsContent>

        <TabsContent value="tips" className="mt-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {TIPS.map((t, i) => (
              <div key={i} className="bg-white rounded-2xl border border-[#dfd8da] p-6 flex items-start gap-3" data-testid={`tip-${i}`}>
                <Lightbulb className="text-[#920e12] shrink-0" aria-hidden="true" />
                <p className="text-[#1a1617]">{t}</p>
              </div>
            ))}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
